/**
 * FormElement の表示レンダラ。
 * 編集画面のプレビュー、公開フォームページの両方で使用。
 *
 * 仕様書: docs/01_spec/spec_line_bot.md A-12（Phase 24b）
 */
import type {
  EnglishProfileValue,
  FieldFormElement,
  FormElement,
  PublicFormDynamicOptions,
} from '@goenhub/shared';
import {
  BRIEFING_STYLE_LABEL,
  COUNTRY_ADDRESS_CONFIGS,
  briefingParticipantsKey,
  decodeEnglishProfile,
  encodeBriefingSlotValue,
  encodeEnglishProfile,
  expandBriefingSlotStyles,
  getCountryAddressConfig,
  parseAddressValue,
  parseBriefingSlotValue,
  resolveMaxSelections,
  serializeAddressValue,
  toSelectedOptions,
} from '@goenhub/shared';

interface FieldRendererProps {
  field: FieldFormElement;
  /** 現在の入力値（公開ページで送信用） */
  value?: string | string[];
  onChange?: (value: string | string[]) => void;
  /** プレビューモード時は disabled にして編集画面で誤操作を防ぐ */
  disabled?: boolean;
  /** ブランドカラー（フォーカス時等のアクセント） */
  brandColor?: string;
  /** Phase 1+: 動的フィールド (briefing_schedule_picker / product_picker / assignee_picker) のサーバー取得 payload */
  dynamicOptions?: PublicFormDynamicOptions;
  /** briefing_schedule_picker 用: 参加人数の現在値 (values の `${fieldId}__participants`) */
  participantsValue?: string | string[];
  /** briefing_schedule_picker 用: 参加人数の変更通知 */
  onParticipantsChange?: (value: string) => void;
  /**
   * 2026-08-31: 送信を試みた後だけ出したい注意表示（checkbox の選択上限未達など）を出す。
   * 公開フォームが送信バリデーションで弾いたときに true にする。
   */
  showValidation?: boolean;
}

/** YYYY-MM-DD → "M/D (曜)" の表示用フォーマット */
function formatJpDate(ymd: string): string {
  const m = ymd.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return ymd;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const days = ['日', '月', '火', '水', '木', '金', '土'];
  return `${d.getMonth() + 1}/${d.getDate()} (${days[d.getDay()]})`;
}

interface ProductPickerEntry {
  productListId: string;
  modelName: string;
  variation?: string | null;
  qty: number;
}

function parseProductPickerValue(raw: string | string[] | undefined): ProductPickerEntry[] {
  if (!raw || typeof raw !== 'string') return [];
  try {
    const parsed = JSON.parse(raw) as ProductPickerEntry[];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

/**
 * checkbox（複数選択）の選択肢群 (2026-08-31: 選択上限に対応)。
 *
 * field.maxSelections が設定されているときの挙動:
 *   - 「選択数 / 上限」を常時表示し、上限に達したら「上限に達しました」を追記
 *   - 上限に達したら未選択の項目を disabled にする（外すのは常に可能）
 *   - 必須なら上限数ちょうど選ぶまで注意文を出す
 *     （初回描画からいきなり赤字を出さないよう、1 つ以上選んだ後か送信試行後のみ）
 */
function CheckboxOptions({
  field,
  value,
  onChange,
  disabled,
  showValidation,
}: {
  field: FieldFormElement;
  value?: string | string[];
  onChange?: (value: string | string[]) => void;
  disabled?: boolean;
  showValidation?: boolean;
}) {
  const selected = toSelectedOptions(value);
  const max = resolveMaxSelections(field);
  const reached = max !== undefined && selected.length >= max;
  const shortfall =
    max !== undefined &&
    field.required &&
    selected.length < max &&
    (showValidation || selected.length > 0);

  return (
    <div className="space-y-1.5">
      {(field.options ?? []).map((opt) => {
        const checked = selected.includes(opt);
        // 上限到達後は未選択の項目だけロックする（選択済みは外せる）
        const lockedOut = reached && !checked;
        return (
          <label
            key={opt}
            className={`flex items-center gap-2 text-[13px] ${
              lockedOut
                ? 'text-gray-400 cursor-not-allowed'
                : 'text-gray-800 cursor-pointer'
            }`}
          >
            <input
              type="checkbox"
              checked={checked}
              onChange={(e) => {
                if (!onChange) return;
                if (e.target.checked) {
                  if (max !== undefined && selected.length >= max) return;
                  onChange([...selected, opt]);
                } else {
                  onChange(selected.filter((x) => x !== opt));
                }
              }}
              disabled={disabled || lockedOut}
              className="size-4"
            />
            {opt}
          </label>
        );
      })}
      {max !== undefined && (
        <p
          className={`text-[11px] pt-0.5 ${
            reached ? 'text-amber-600' : 'text-gray-500'
          }`}
        >
          {selected.length}/{max}
          {reached && <span className="ml-1.5">上限に達しました</span>}
        </p>
      )}
      {shortfall && max !== undefined && (
        <p className="text-[11px] text-red-600">
          {max} つ選択してください（あと {max - selected.length} つ）
        </p>
      )}
    </div>
  );
}

function FieldRenderer({
  field,
  value,
  onChange,
  disabled,
  brandColor,
  dynamicOptions,
  participantsValue,
  onParticipantsChange,
  showValidation,
}: FieldRendererProps) {
  const baseInputClass =
    'w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-[14px] text-gray-900 placeholder:text-gray-400 focus:outline-none focus:border-2';
  const focusStyle = brandColor
    ? { borderColor: undefined, '--tw-ring-color': brandColor } as React.CSSProperties
    : undefined;

  const requiredMark = field.required ? (
    <span className="text-red-500 ml-1">*</span>
  ) : null;

  if (field.fieldType === 'consent') {
    return (
      <label className="flex items-start gap-2 cursor-pointer">
        <input
          type="checkbox"
          checked={value === 'true'}
          onChange={(e) => onChange?.(e.target.checked ? 'true' : '')}
          disabled={disabled}
          className="size-4 mt-0.5"
        />
        <span className="text-[13px] text-gray-800">
          {field.consentText || field.label}
          {requiredMark}
        </span>
      </label>
    );
  }

  // 返答要求 (2026-07-23): 単一チェックボックス。値は 'true' / ''
  if (field.fieldType === 'reply_request') {
    return (
      <div>
        <label className="flex items-start gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={value === 'true'}
            onChange={(e) => onChange?.(e.target.checked ? 'true' : '')}
            disabled={disabled}
            className="size-4 mt-0.5"
          />
          <span className="text-[13px] text-gray-800">
            {field.label}
            {requiredMark}
          </span>
        </label>
        {field.helpText && (
          <p className="text-[11px] text-gray-500 mt-1">{field.helpText}</p>
        )}
      </div>
    );
  }

  // ─── 動的フィールド (Phase 1+) ───
  if (field.fieldType === 'briefing_schedule_picker') {
    const allSchedules = dynamicOptions?.briefingSchedules ?? [];
    const styleFilter = field.filterStyle;
    const schedules = styleFilter
      ? allSchedules.filter(
          (s) => s.style === styleFilter || s.style === 'both' || styleFilter === 'both',
        )
      : allSchedules;
    const selected = Array.isArray(value) ? value : [];

    // 「両方可 (both)」の枠は「オンライン」「現地」の2択に分割して表示する (2026-08-10)。
    // 分割した選択肢の value は `${枠id}::online` / `${枠id}::onsite` (単独スタイル枠は枠id のまま)。
    // 同一枠は1予約なので、片方を選ぶともう片方は自動的に外れる。
    const options = schedules.flatMap((s) =>
      expandBriefingSlotStyles(s.style, styleFilter).map((v) => ({
        slot: s,
        style: v.style,
        value: encodeBriefingSlotValue(s.id, v.splitFromBoth ? v.style : null),
      })),
    );

    // ─── 参加人数 (受入人数 = 1組の同時参加可能人数。1枠=1予約で人数に依らず確定でクローズ) ───
    const slotsOf = (values: string[]) => {
      const ids = new Set(values.map((v) => parseBriefingSlotValue(v).scheduleId));
      return schedules.filter((s) => ids.has(s.id));
    };
    const selectedSlots = slotsOf(selected);
    // 上限: 選択済み枠があればその受入人数の最小値 / 未選択なら表示中の枠の受入人数の最大値
    const maxParticipants = Math.max(
      1,
      selectedSlots.length > 0
        ? Math.min(...selectedSlots.map((s) => s.capacity))
        : options.length > 0
          ? Math.max(...options.map((o) => o.slot.capacity))
          : 1,
    );
    const rawCount =
      typeof participantsValue === 'string'
        ? parseInt(participantsValue, 10)
        : NaN;
    const participantCount = Math.min(
      maxParticipants,
      Math.max(1, Number.isFinite(rawCount) ? rawCount : 1),
    );
    // 枠の選択変更で上限が下がった場合は自動クランプ
    const handleSlotToggle = (
      optionValue: string,
      slotId: string,
      checked: boolean,
    ) => {
      if (!onChange) return;
      // 同一枠の別スタイル (両方可の分割) は同時選択させない
      const withoutSlot = selected.filter(
        (v) => parseBriefingSlotValue(v).scheduleId !== slotId,
      );
      const next = checked ? [...withoutSlot, optionValue] : withoutSlot;
      onChange(next);
      const nextSlots = slotsOf(next);
      if (nextSlots.length > 0 && onParticipantsChange) {
        const nextMax = Math.max(
          1,
          Math.min(...nextSlots.map((s) => s.capacity)),
        );
        if (participantCount > nextMax) {
          onParticipantsChange(String(nextMax));
        }
      }
    };

    return (
      <div>
        <label className="block text-[13px] font-medium text-gray-800 mb-1">
          {field.label}
          {requiredMark}
        </label>
        {options.length === 0 ? (
          <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
            現在受付可能な日程はありません。
          </div>
        ) : (
          <div className="space-y-1.5 max-h-72 overflow-y-auto rounded-md border border-gray-200 p-2">
            {options.map((o) => {
              const s = o.slot;
              const checked = selected.includes(o.value);
              const styleLabel = BRIEFING_STYLE_LABEL[o.style];
              return (
                <label
                  key={o.value}
                  className="flex items-center gap-2 text-[13px] cursor-pointer hover:bg-gray-50 rounded px-2 py-1"
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={(e) =>
                      handleSlotToggle(o.value, s.id, e.target.checked)
                    }
                    disabled={disabled}
                    className="size-4"
                  />
                  <span className="tabular-nums">
                    {formatJpDate(s.date)} {s.startTime}〜{s.endTime}
                  </span>
                  <span className="text-[11px] text-gray-500">· {styleLabel}</span>
                  {o.style === 'onsite' && s.location && (
                    <span className="text-[11px] text-gray-500">@ {s.location}</span>
                  )}
                  <span className="ml-auto text-[10px] text-gray-400 tabular-nums">
                    受入{s.capacity}名
                  </span>
                </label>
              );
            })}
          </div>
        )}
        {options.length > 0 && (
          <div className="mt-2 flex items-center justify-between gap-3 rounded-md border border-gray-200 bg-gray-50 px-3 py-2">
            <div className="min-w-0">
              <span className="text-[13px] font-medium text-gray-800">
                参加人数
              </span>
              <p className="text-[11px] text-gray-500 mt-0.5">
                {selectedSlots.length > 0
                  ? `選択中の枠は最大 ${maxParticipants} 名まで参加できます`
                  : '同時に参加される人数をお選びください'}
              </p>
            </div>
            <div className="flex items-center gap-1.5 flex-shrink-0">
              <select
                value={String(participantCount)}
                onChange={(e) => onParticipantsChange?.(e.target.value)}
                disabled={disabled}
                className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-[14px] text-gray-900"
                aria-label="参加人数"
              >
                {Array.from({ length: maxParticipants }, (_, i) => i + 1).map(
                  (n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ),
                )}
              </select>
              <span className="text-[13px] text-gray-700">名</span>
            </div>
          </div>
        )}
        {field.helpText && (
          <p className="text-[11px] text-gray-500 mt-1">{field.helpText}</p>
        )}
      </div>
    );
  }

  if (field.fieldType === 'product_picker') {
    const products = dynamicOptions?.products ?? [];
    const entries = parseProductPickerValue(value);

    const productKey = (p: { productListId: string; modelName: string; variation?: string | null }) =>
      `${p.productListId}::${p.modelName}::${p.variation ?? ''}`;

    const updateEntries = (next: ProductPickerEntry[]) => {
      onChange?.(JSON.stringify(next.filter((e) => e.qty > 0)));
    };

    return (
      <div>
        <label className="block text-[13px] font-medium text-gray-800 mb-1">
          {field.label}
          {requiredMark}
        </label>
        {products.length === 0 ? (
          <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
            現在販売中の製品が登録されていません。
          </div>
        ) : (
          <div className="space-y-1.5 max-h-72 overflow-y-auto rounded-md border border-gray-200 p-2">
            {products.map((p) => {
              const key = productKey(p);
              const entry = entries.find(
                (e) =>
                  e.productListId === p.productListId &&
                  e.modelName === p.modelName &&
                  (e.variation ?? '') === (p.variation ?? ''),
              );
              const qty = entry?.qty ?? 0;
              return (
                <div
                  key={key}
                  className="flex items-center gap-2 text-[13px] px-2 py-1 hover:bg-gray-50 rounded"
                >
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">{p.name}</div>
                    <div className="text-[11px] text-gray-500 font-mono">
                      {p.modelName}
                      {p.variation ? ` / ${p.variation}` : ''}
                    </div>
                  </div>
                  <span className="text-[11px] text-gray-700 tabular-nums">
                    ¥{p.priceIncludingTax.toLocaleString()}
                  </span>
                  <input
                    type="number"
                    min={0}
                    max={99}
                    value={qty}
                    onChange={(e) => {
                      const newQty = Math.max(0, Number(e.target.value) || 0);
                      const next = entries.filter(
                        (en) =>
                          !(
                            en.productListId === p.productListId &&
                            en.modelName === p.modelName &&
                            (en.variation ?? '') === (p.variation ?? '')
                          ),
                      );
                      if (newQty > 0) {
                        next.push({
                          productListId: p.productListId,
                          modelName: p.modelName,
                          variation: p.variation ?? null,
                          qty: newQty,
                        });
                      }
                      updateEntries(next);
                    }}
                    disabled={disabled}
                    className="w-14 rounded border border-gray-300 px-2 py-0.5 text-[12px] text-right tabular-nums"
                  />
                </div>
              );
            })}
          </div>
        )}
        {field.helpText && (
          <p className="text-[11px] text-gray-500 mt-1">{field.helpText}</p>
        )}
      </div>
    );
  }

  // 2026-05-28: 構造化住所フィールド (billing_address / delivery_address)
  if (
    field.fieldType === 'billing_address' ||
    field.fieldType === 'delivery_address'
  ) {
    const parts = parseAddressValue(typeof value === 'string' ? value : '');
    const update = (patch: Partial<typeof parts>) => {
      const next = { ...parts, ...patch };
      onChange?.(serializeAddressValue(next));
    };
    return (
      <div>
        <label className="block text-[13px] font-medium text-gray-800 mb-1">
          {field.label}
          {requiredMark}
        </label>
        <div className="space-y-2">
          <input
            type="text"
            value={parts.postalCode}
            onChange={(e) => update({ postalCode: e.target.value })}
            placeholder="郵便番号 (例: 1500001)"
            disabled={disabled}
            className={baseInputClass}
            style={focusStyle}
          />
          <input
            type="text"
            value={parts.prefecture}
            onChange={(e) => update({ prefecture: e.target.value })}
            placeholder="都道府県"
            disabled={disabled}
            className={baseInputClass}
            style={focusStyle}
          />
          <input
            type="text"
            value={parts.city}
            onChange={(e) => update({ city: e.target.value })}
            placeholder="市区町村"
            disabled={disabled}
            className={baseInputClass}
            style={focusStyle}
          />
          <input
            type="text"
            value={parts.line}
            onChange={(e) => update({ line: e.target.value })}
            placeholder="町名・番地・建物名"
            disabled={disabled}
            className={baseInputClass}
            style={focusStyle}
          />
        </div>
        {field.helpText && (
          <p className="text-[11px] text-gray-500 mt-1">{field.helpText}</p>
        )}
      </div>
    );
  }

  // 2026-07: 英表記登録情報 (english_profile)
  if (field.fieldType === 'english_profile') {
    const v = decodeEnglishProfile(typeof value === 'string' ? value : '');
    const config = getCountryAddressConfig(v.country);
    const update = (patch: Partial<EnglishProfileValue>) => {
      onChange?.(encodeEnglishProfile({ ...v, ...patch }));
    };
    return (
      <div>
        <label className="block text-[13px] font-medium text-gray-800 mb-1">
          {field.label}
          {requiredMark}
        </label>
        <div className="space-y-2">
          <input
            type="text"
            value={v.nameEn}
            onChange={(e) => update({ nameEn: e.target.value })}
            placeholder="Name in English (英語名)"
            disabled={disabled}
            className={baseInputClass}
            style={focusStyle}
          />
          <input
            type="tel"
            value={v.phoneIntl}
            onChange={(e) => update({ phoneIntl: e.target.value })}
            placeholder="Phone with country code (+81 90-1234-5678)"
            disabled={disabled}
            className={baseInputClass}
            style={focusStyle}
          />
          <select
            value={v.country}
            onChange={(e) => update({ country: e.target.value })}
            disabled={disabled}
            className={`${baseInputClass} text-gray-800`}
            style={focusStyle}
          >
            <option value="">Country (国を選択)</option>
            {COUNTRY_ADDRESS_CONFIGS.map((c) => (
              <option key={c.code} value={c.code}>
                {c.name}
              </option>
            ))}
          </select>
          {v.country &&
            config.fields.map((f) => (
              <input
                key={f.key}
                type="text"
                value={v[f.key]}
                onChange={(e) =>
                  update({ [f.key]: e.target.value } as Partial<EnglishProfileValue>)
                }
                placeholder={
                  f.placeholder ? `${f.label} (${f.placeholder})` : f.label
                }
                disabled={disabled}
                className={baseInputClass}
                style={focusStyle}
              />
            ))}
        </div>
        {field.helpText && (
          <p className="text-[11px] text-gray-500 mt-1">{field.helpText}</p>
        )}
      </div>
    );
  }

  if (field.fieldType === 'assignee_picker') {
    const assignees = dynamicOptions?.assignees ?? [];
    return (
      <div>
        <label className="block text-[13px] font-medium text-gray-800 mb-1">
          {field.label}
          {requiredMark}
        </label>
        {assignees.length === 0 ? (
          <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
            担当者が登録されていません。
          </div>
        ) : (
          <select
            value={(value as string) ?? ''}
            onChange={(e) => onChange?.(e.target.value)}
            disabled={disabled}
            required={field.required}
            className={baseInputClass}
            style={focusStyle}
          >
            <option value="">選択してください</option>
            {assignees.map((a) => (
              <option key={a.uid} value={a.uid}>
                {a.displayName}
              </option>
            ))}
          </select>
        )}
        {field.helpText && (
          <p className="text-[11px] text-gray-500 mt-1">{field.helpText}</p>
        )}
      </div>
    );
  }

  return (
    <div>
      <label className="block text-[13px] font-medium text-gray-800 mb-1">
        {field.label}
        {requiredMark}
      </label>
      {field.fieldType === 'textarea' ? (
        <textarea
          rows={4}
          value={(value as string) ?? ''}
          onChange={(e) => onChange?.(e.target.value)}
          placeholder={field.placeholder}
          disabled={disabled}
          required={field.required}
          className={`${baseInputClass} min-h-[100px] leading-relaxed`}
          style={focusStyle}
        />
      ) : field.fieldType === 'select' ? (
        <select
          value={(value as string) ?? ''}
          onChange={(e) => onChange?.(e.target.value)}
          disabled={disabled}
          required={field.required}
          className={baseInputClass}
          style={focusStyle}
        >
          <option value="">選択してください</option>
          {(field.options ?? []).map((opt) => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </select>
      ) : field.fieldType === 'radio' ? (
        <div className="space-y-1.5">
          {(field.options ?? []).map((opt) => (
            <label
              key={opt}
              className="flex items-center gap-2 text-[13px] text-gray-800 cursor-pointer"
            >
              <input
                type="radio"
                name={field.id}
                value={opt}
                checked={value === opt}
                onChange={() => onChange?.(opt)}
                disabled={disabled}
                required={field.required}
                className="size-4"
              />
              {opt}
            </label>
          ))}
        </div>
      ) : field.fieldType === 'checkbox' ? (
        <CheckboxOptions
          field={field}
          value={value}
          onChange={onChange}
          disabled={disabled}
          showValidation={showValidation}
        />
      ) : (
        <input
          type={
            field.fieldType === 'email'
              ? 'email'
              : field.fieldType === 'tel' || field.fieldType === 'phone'
                ? 'tel'
                : field.fieldType === 'number'
                  ? 'number'
                  : field.fieldType === 'date' ||
                      field.fieldType === 'birthdate'
                    ? 'date'
                    : 'text'
          }
          value={(value as string) ?? ''}
          onChange={(e) => onChange?.(e.target.value)}
          placeholder={field.placeholder}
          disabled={disabled}
          required={field.required}
          className={baseInputClass}
          style={focusStyle}
        />
      )}
      {field.helpText && (
        <p className="text-[11px] text-gray-500 mt-1">{field.helpText}</p>
      )}
    </div>
  );
}

interface ElementRendererProps {
  element: FormElement;
  values: Record<string, string | string[]>;
  onChange?: (id: string, value: string | string[]) => void;
  disabled?: boolean;
  brandColor?: string;
  /** Phase 1+: 動的フィールド payload (公開フォームのみ) */
  dynamicOptions?: PublicFormDynamicOptions;
  /** 2026-08-31: 送信試行後に出す注意表示（checkbox の選択上限未達など）を有効にする */
  showValidation?: boolean;
}

export function FormElementRenderer({
  element,
  values,
  onChange,
  disabled,
  brandColor,
  dynamicOptions,
  showValidation,
}: ElementRendererProps) {
  if (element.kind === 'field') {
    return (
      <FieldRenderer
        field={element}
        value={values[element.id]}
        onChange={(v) => onChange?.(element.id, v)}
        disabled={disabled}
        brandColor={brandColor}
        dynamicOptions={dynamicOptions}
        participantsValue={values[briefingParticipantsKey(element.id)]}
        onParticipantsChange={(v) =>
          onChange?.(briefingParticipantsKey(element.id), v)
        }
        showValidation={showValidation}
      />
    );
  }
  if (element.kind === 'title') {
    const Tag = element.level;
    const sizeClass =
      element.level === 'h1'
        ? 'text-2xl font-bold'
        : element.level === 'h2'
          ? 'text-xl font-bold'
          : 'text-base font-semibold';
    return <Tag className={`${sizeClass} text-gray-900`}>{element.text}</Tag>;
  }
  if (element.kind === 'body') {
    return (
      <p className="text-[13px] text-gray-700 leading-relaxed whitespace-pre-wrap">
        {element.text}
      </p>
    );
  }
  if (element.kind === 'image') {
    if (!element.imageUrl) return null;
    return (
      <img
        src={element.imageUrl}
        alt={element.alt ?? ''}
        className="max-w-full h-auto rounded"
        style={{ maxHeight: 400 }}
      />
    );
  }
  if (element.kind === 'divider') {
    return <hr className="border-t border-gray-200 my-2" />;
  }
  return null;
}
